import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  Dialog,
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import { errorText } from "components/Forms/formsApi";
import { formatDateTime } from "components/Forms/ResponseDialog";
import { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Notifications from "util/notifications";
import { ActionPill, actionWords, WaitingPill } from "./ActivityPill";
import ActivityTabChange, {
  ChangeDraft,
  draftOf,
  editsOf,
} from "./ActivityTabChange";
import {
  aboutOf,
  ActivityTable,
  approverWords,
  askedWho,
  AutomationPill,
  CELL,
  DetailsPopover,
  PeoplePills,
  PreviewButton,
  PreviewLine,
  PreviewParts,
  proposalTitle,
  useAutomationIndex,
} from "./ActivityTabParts";
import { useAutomationsApi, useReference } from "./hooks";
import { columnById } from "./reference";
import type {
  ActivityDetails,
  ApprovalAnswer,
  ApprovalItem,
  ProposalEdits,
  Trigger,
} from "./types";
import { COPY } from "./words";

/*
 * Waiting for approval's page of the table: the same columns as Done, its
 * preview what approving would do now, and Approve and Skip at the right of
 * each row. Approve does what the card's Add (Send, Move, Update) does;
 * Skip is its Not now. The pop-up shows it all, with Change it first (a
 * task or CRM item) or Edit template (a message).
 */

/** An item with no preview yet: nothing more than its title to say. */
const NO_PREVIEW: ActivityDetails = {
  message: null,
  tasks: [],
  move: null,
  fields: [],
  note: null,
};

/**
 * What an item would do, in its pop-up: its title, what started it, why it
 * waits besides Ask first, or why it can't be done now; then its action's
 * part (PreviewParts: a message's To, By and wording as it would go out, a
 * task's people and due date, a move's From and To, each field before and
 * after) with a task's About and description and a move's board; who was
 * asked and when, and who can approve; and its answers.
 */
function WaitingDetails({
  item,
  busy,
  onAnswer,
  onChangeFirst,
}: {
  item: ApprovalItem;
  busy: boolean;
  onAnswer: (answer: ApprovalAnswer) => void;
  onChangeFirst: () => void;
}) {
  const ref = useReference();
  const p = item.proposal;
  const title = proposalTitle(ref, p);
  const extra: [string, ReactNode][] = [];
  if (p.action === "task") {
    const about = aboutOf(item);
    if (about.length) extra.push(["About", <PeoplePills people={about} />]);
    if (p.description.trim())
      extra.push([
        "Description",
        <span className="whitespace-pre-wrap">{p.description}</span>,
      ]);
  }
  if (p.action === "crm_move")
    extra.push([
      "Board",
      columnById(ref, p.boardId, p.columnId)?.board.name ?? p.columnLabel,
    ]);
  const notes = [
    item.note,
    item.preview?.note,
    p.action === "task" && p.fallback ? COPY.fallbackNote(ref) : null,
  ].filter((note): note is string => !!note);
  const templateId = p.action === "message" ? p.templateId : null;
  const secondary = {
    color: ButtonColor.GREEN,
    fill: ButtonFill.HOLLOW,
    size: ButtonSize.SMALL,
    disabled: busy,
  };
  return (
    <div className="space-y-3 p-3 text-sm">
      <div>
        <p className="break-words font-semibold text-gray-900">{title}</p>
        {item.reason && (
          <p className="mt-0.5 break-words text-gray-600">{item.reason}</p>
        )}
        {notes.map((note) => (
          <p key={note} className="mt-0.5 break-words text-amber-800">
            {note}
          </p>
        ))}
      </div>
      <PreviewParts
        action={p.action}
        details={item.preview ?? NO_PREVIEW}
        title={title}
        extra={extra}
      />
      <p className="text-xs text-gray-500">
        Asked {askedWho(ref, item.askedOf)} on {formatDateTime(item.askedAt)}
        {/* Admins can approve every item, so only a wider choice is said. */}
        {item.approvers && item.approvers !== "admins" && (
          <span className="block">{approverWords(item.approvers)}</span>
        )}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          color={ButtonColor.GREEN}
          size={ButtonSize.SMALL}
          disabled={busy}
          onClick={() => onAnswer({ decision: "approve" })}
        >
          Approve
        </Button>
        {p.action !== "message" && (
          <Button {...secondary} onClick={onChangeFirst}>
            Change it first
          </Button>
        )}
        <Button
          {...secondary}
          onClick={() => onAnswer({ decision: "dismiss" })}
        >
          Skip
        </Button>
        {templateId !== null && (
          <Link
            to={`/message-templates/${templateId}`}
            className="self-center text-sm font-semibold text-green-700 hover:underline"
          >
            Edit template
          </Link>
        )}
      </div>
    </div>
  );
}

/** One row: Waiting; its Action and Preview, each opening its pop-up; About; Automation; When it asked; Approve and Skip. */
function WaitingRow({
  item,
  automations,
  busy,
  onAnswer,
  onChangeFirst,
}: {
  item: ApprovalItem;
  automations: ReturnType<typeof useAutomationIndex>;
  busy: boolean;
  onAnswer: (answer: ApprovalAnswer) => void;
  onChangeFirst: () => void;
}) {
  const ref = useReference();
  const action = item.proposal.action;
  const title = proposalTitle(ref, item.proposal);
  const word = actionWords(action, false);
  const details = item.preview ?? NO_PREVIEW;
  // Wider for a message's wording.
  const wide = !!details.message?.sent.length;
  const popUp = (close: () => void) => (
    <WaitingDetails
      item={item}
      busy={busy}
      onAnswer={(answer) => {
        close();
        onAnswer(answer);
      }}
      onChangeFirst={() => {
        close();
        onChangeFirst();
      }}
    />
  );
  const automationId = item.automationId || null;
  return (
    <tr
      className={`border-b border-gray-200 transition hover:bg-gray-50 ${
        busy ? "opacity-50" : ""
      }`}
    >
      <td className={CELL}>
        <WaitingPill item={item} />
      </td>
      <td className={CELL}>
        <DetailsPopover
          label={title}
          wide={wide}
          trigger={
            <ActionPill
              action={action}
              label={word}
              aria-label={`${word}: ${title}`}
            />
          }
        >
          {popUp}
        </DetailsPopover>
      </td>
      <td className={CELL}>
        <DetailsPopover
          label={title}
          wide={wide}
          trigger={
            <PreviewButton>
              <PreviewLine action={action} details={details} title={title} />
            </PreviewButton>
          }
        >
          {popUp}
        </DetailsPopover>
      </td>
      <td className={CELL}>
        {item.about && <PeoplePills people={[item.about]} />}
      </td>
      <td className={CELL}>
        <AutomationPill
          id={automationId}
          name={automations.name(automationId, item.automationName)}
          live={automations.live(automationId)}
        />
      </td>
      <td className={`${CELL} whitespace-nowrap text-sm text-gray-500`}>
        {formatDateTime(item.askedAt)}
      </td>
      <td className={CELL}>
        <div className="flex justify-end gap-2">
          <Button
            color={ButtonColor.GREEN}
            size={ButtonSize.SMALL}
            disabled={busy}
            data-row-approve=""
            aria-label={`Approve: ${title}`}
            onClick={() => onAnswer({ decision: "approve" })}
          >
            Approve
          </Button>
          <Button
            color={ButtonColor.GREEN}
            fill={ButtonFill.HOLLOW}
            size={ButtonSize.SMALL}
            disabled={busy}
            aria-label={`Skip: ${title}`}
            onClick={() => onAnswer({ decision: "dismiss" })}
          >
            Skip
          </Button>
        </div>
      </td>
    </tr>
  );
}

/**
 * Change it first, over the page: a task's or CRM item's fields, filled in
 * with what it would do; Approve does it with the changes.
 */
function ChangeFirst({
  item,
  trigger,
  busy,
  onApprove,
  onClose,
}: {
  item: ApprovalItem;
  trigger: Trigger | null;
  busy: boolean;
  onApprove: (edits: ProposalEdits) => void;
  onClose: () => void;
}) {
  const ref = useReference();
  const dialog = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<ChangeDraft | null>(() =>
    draftOf(ref, item)
  );
  const [problem, setProblem] = useState<string | null>(null);
  if (!draft) return null;
  const approve = () => {
    const checked = editsOf(ref, item, draft);
    if ("problem" in checked) {
      setProblem(checked.problem);
      return;
    }
    setProblem(null);
    onApprove(checked.edits);
  };
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        ref={dialog}
        className="dialog-content max-w-[560px]"
        aria-label="Change it first"
      >
        <h2 className="mb-4 text-xl font-semibold">Change it first</h2>
        <ActivityTabChange
          item={item}
          draft={draft}
          onChange={setDraft}
          trigger={trigger}
          // The calendar opens inside the dialog, so picking a day keeps it open.
          dateContainer={() => dialog.current ?? document.body}
        />
        {problem && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {problem}
          </p>
        )}
        <DialogActions>
          <DialogAction
            primary={true}
            color={ButtonColor.GREEN}
            disabled={busy}
            onClick={approve}
          >
            Approve
          </DialogAction>
          <DialogClose asChild>
            <DialogAction color={ButtonColor.SKYBLUE}>Cancel</DialogAction>
          </DialogClose>
        </DialogActions>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Waiting for approval's page of the table. Each answer is sent once (a
 * double click sends one), its row dims while it goes, and the toast says
 * what happened; then focus goes to the row that took its place. faded:
 * another page or filter is loading.
 */
export default function ActivityTabWaiting({
  items,
  faded,
}: {
  items: ApprovalItem[];
  faded: boolean;
}) {
  const api = useAutomationsApi();
  const ref = useReference();
  const automations = useAutomationIndex();
  // Items whose answer is on its way.
  const sending = useRef(new Set<string>());
  const [answering, setAnswering] = useState<ReadonlySet<string>>(new Set());
  const [changing, setChanging] = useState<string | null>(null);
  const table = useRef<HTMLDivElement>(null);
  const [refocus, setRefocus] = useState<{ id: string; at: number } | null>(
    null
  );

  // Once the answered row has left, focus that went with it goes to the
  // row that took its place (or the one before it). Focus moved elsewhere
  // meanwhile stays where it is.
  useEffect(() => {
    if (!refocus || items.some((item) => item.id === refocus.id)) return;
    setRefocus(null);
    const focused = document.activeElement;
    if (focused && focused !== document.body) return;
    const buttons =
      table.current?.querySelectorAll<HTMLButtonElement>("[data-row-approve]");
    if (buttons?.length)
      buttons[Math.min(refocus.at, buttons.length - 1)].focus();
  }, [refocus, items]);

  /** Sends an answer; whether it went through. */
  const answer = (item: ApprovalItem, reply: ApprovalAnswer) => {
    if (sending.current.has(item.id)) return Promise.resolve(false);
    sending.current.add(item.id);
    setAnswering(new Set(sending.current));
    const at = items.findIndex((other) => other.id === item.id);
    return api
      .answerApproval(item.id, reply)
      .then((result) => {
        Notifications.success(
          reply.decision === "dismiss"
            ? `${proposalTitle(ref, item.proposal)} was skipped.`
            : result.summary
        );
        setRefocus({ id: item.id, at });
        return true;
      })
      .catch((e) => {
        Notifications.error(errorText(e, "That didn't go through. Try again."));
        return false;
      })
      .finally(() => {
        sending.current.delete(item.id);
        setAnswering(new Set(sending.current));
      });
  };

  const changed = items.find((item) => item.id === changing) ?? null;

  return (
    <div ref={table}>
      <ActivityTable answers faded={faded}>
        {items.map((item) => (
          <WaitingRow
            key={item.id}
            item={item}
            automations={automations}
            busy={answering.has(item.id)}
            onAnswer={(reply) => answer(item, reply)}
            onChangeFirst={() => setChanging(item.id)}
          />
        ))}
      </ActivityTable>
      {changed && (
        <ChangeFirst
          key={changed.id}
          item={changed}
          trigger={
            automations.automation(changed.automationId)?.trigger ?? null
          }
          busy={answering.has(changed.id)}
          onApprove={(edits) =>
            answer(changed, { decision: "approve", edits }).then(
              (done) => done && setChanging(null)
            )
          }
          onClose={() => setChanging(null)}
        />
      )}
    </div>
  );
}
